/**
 * useBranding — the ONLY way Vue components should access brand-specific
 * values. Never call import.meta.env.VITE_BRAND_* directly in a component.
 *
 * Wraps the static `branding` config and re-exposes it as flat fields plus a
 * few derived conveniences (contact email aliases, environmentList) so
 * components don't need to know the underlying config shape. Returns brand
 * identity, assets, contact info, domains, beta/waitlist URL, UI messaging,
 * legal links, auth integration URLs/keys, and theme/font/SEO values.
 *
 * USAGE:
 *   import { useBranding } from '@/composables/useBranding'
 *
 *   const {
 *     brandName, tagline, companyName, website,
 *     logo, contact, domains,
 *     betaUsersUrl, messaging, legal,
 *     siwfLoginUrl, recaptchaSiteKey, themePreset
 *   } = useBranding()
 *
 * NOT IMPORTED ANYWHERE YET — ready for future white-label activation.
 */

import { branding } from '@/config/branding';

/** Returns the current brand's identity, assets, contact, domain, messaging, legal, auth, and theme values. */
export function useBranding() {
  return {
    // ── Identity ────────────────────────────────────────────────────────────
    brandName:   branding.name,
    tagline:     branding.tagline,
    companyName: branding.companyName,
    website:     branding.website,

    // ── Assets ──────────────────────────────────────────────────────────────
    // logo.main    → main navbar/header logo
    // logo.icon    → small square icon (mobile, favicon fallback)
    // logo.favicon → /public path for <link rel="icon">
    // logo.ogImage → OpenGraph share image
    logo: branding.logo,

    // ── Contact ─────────────────────────────────────────────────────────────
    // contact.supportEmail  → shown in help, error pages, onboarding
    // contact.reportEmail   → abuse report flows
    // contact.helpEmail     → help/contact pages (CreateUsername.vue, VerificationCode.vue)
    // contact.helpUsername  → brand's help account handle (dougjoneshelp.vue)
    // contact.brandUsername → brand's main account handle
    contact: branding.contact,

    // Shorthand aliases for the most-used contact values
    supportEmail:  branding.contact.supportEmail,
    brandEmail:    branding.contact.brandEmail,
    reportEmail:   branding.contact.reportEmail,
    helpEmail:     branding.contact.helpEmail,
    helpUsername:  branding.contact.helpUsername,
    brandUsername: branding.contact.brandUsername,

    // ── Domains ─────────────────────────────────────────────────────────────
    // domains.prod    → used in Leftbar environment switcher and Post.vue domain check
    // domains.staging → staging environment URL
    // domains.dev     → dev environment URL
    domains: branding.domains,

    // Array form for the Leftbar environment switcher (Leftbar/index.vue:176)
    // Replace: const environments = ref([])
    environmentList: [
      branding.domains.dev,
      branding.domains.staging,
      branding.domains.prod,
    ].filter(Boolean),

    // ── Beta / waitlist ──────────────────────────────────────────────────────
    // Used by: AboutUs/index.vue:61, declarationPage.vue:51, termsOfService.vue:52
    betaUsersUrl: branding.betaUsersUrl,

    // ── UI Messaging ─────────────────────────────────────────────────────────
    // messaging.welcomeTitle         → onboarding / about page heading
    // messaging.smsConsent           → SMS verification consent text
    // messaging.teamSignature        → email / reply signatures
    // messaging.communityName        → "Thank you for looking out for the <X> community"
    // messaging.helpPageTitle        → dougjoneshelp.vue page heading
    // messaging.externalAccountConsent → connectExternalAccountsModal.vue:45
    messaging: branding.messaging,

    // ── Legal ────────────────────────────────────────────────────────────────
    // legal.privacyUrl   → link to privacy policy page
    // legal.termsUrl     → link to terms of service page
    // legal.legalEmail   → contact email used inside legal documents
    legal: branding.legal,

    // ── Auth integrations ────────────────────────────────────────────────────
    // Used by: Header/index.vue:558, LandingPage.vue:105, BaseModal, connectExternalAccountsModal
    siwfLoginUrl:     branding.siwfLoginUrl,
    // Used in index.html <script src="...?render=KEY">
    recaptchaSiteKey: branding.recaptchaSiteKey,

    // ── Theme & fonts ────────────────────────────────────────────────────────
    // themePreset must match a key in src/config/theme.js (default | douglasjones | unimiddle)
    themePreset: branding.themePreset,
    fontFamily:  branding.fontFamily,

    // ── SEO ─────────────────────────────────────────────────────────────────
    seo: branding.seo,
  };
}
